<template>
	<div class="counter" :class="type ? `counter--${type}` : ''">
		<CounterButton
			:disabled="decDisabled"
			type="decrement"
			@button-click="onButtonClick"
		/>
		<CounterInput :value="valueRef" @input="onInput" />
		<CounterButton
			:disabled="incDisabled"
			type="increment"
			@button-click="onButtonClick"
		/>
	</div>
</template>

<script setup lang="ts">
import { computed, defineEmits, ref, watch } from 'vue';
import CounterButton from './counter-component-button.vue';
import CounterInput from './counter-component-input.vue';

interface Props {
	type?: string;
	value?: number;
	min?: number;
	max?: number;
}

const props = defineProps<Props>();

const emit = defineEmits(['change']);

const valueRef = ref<number>(props.value ?? 0);

watch(
	() => props.value,
	(val) => {
		if (typeof val === 'number') valueRef.value = normalize(val);
	}
);

const hasMax = computed(() => props.max !== undefined);

const normalize = (val: number): number => {
	if (isNaN(val)) return valueRef.value;

	if (val < (props.min ?? 0)) return props.min ?? 0;
	if (hasMax.value && val > props.max!) return props.max!;

	return val;
};

const decDisabled = computed(() => valueRef.value <= (props.min ?? 0));
const incDisabled = computed(() => hasMax.value && valueRef.value >= props.max!);

const onInput = (eventValue: string): void => {
	const parsed = parseInt(eventValue, 10);
	valueRef.value = normalize(parsed);
	emit('change', valueRef.value);
};

const onButtonClick = (type: 'increment' | 'decrement'): void => {
	const next = type === 'decrement' ? valueRef.value - 1 : valueRef.value + 1;

	valueRef.value = normalize(next);
	emit('change', valueRef.value);
};
</script>
